import { sortBy, uniqueId } from 'lodash';
import * as api from '../api';
import * as types from '../constants/actions';
import { buildCall, decodePostResponseId, doValidate } from '../utils/asset';
import { saveStatusClear, saveStatusStart, saveStatusStep, saveAddError, saveClearErrors } from './saveStatus';

export const getRef = (type, ids, sort = 0, required = {}) => {
  return (dispatch) => {
    const ref = uniqueId('ref-');

    dispatch({
      type: types.INIT_REF,
      payload: { ref, type, ids, sort, required }
    });

    return ref;
  };
};

export const removeRef = (ref) => (dispatch) => dispatch({ type: types.REMOVE_REF, payload: { ref } });
export const removeRefs = () => (dispatch) => dispatch({ type: types.REMOVE_REFS });

export const updateChangeId = (ref, id) => (dispatch) => dispatch({
  type: types.UPDATE_CHANGE_ID,
  payload: { ref, id }
});

export const updateRequiredFields = (ref, required = {}) => (dispatch) => dispatch({
  type: types.UPDATE_REQUIRED_CHANGES,
  payload: { ref, required }
});

export const addChange = (ref, changes) => (dispatch) => dispatch({
  type: types.ADD_PENDING_CHANGES,
  payload: { ref, changes }
});

export const addValidationError = (ref, field, errors) => (dispatch) => dispatch({
  type: types.VALIDATION_ADD_ERROR,
  payload: { ref, field, errors }
});
export const clearValidationErrors = () => (dispatch) => dispatch({ type: types.VALIDATION_CLEAR_ERRORS });

export const validateChanges = (allChanges) => (dispatch) => {
  let successful = true;

  allChanges.forEach((change) => {
    // Include required fields
    const changes = Object.assign(change.required, change.changes);

    // If there are ANY changes for the record check it.
    // Otherwise ignore the required fields and consider it an unchanged record
    if (Object.keys(change.changes || {}).length !== 0) {
      for (const field in changes) {
        if (Object.prototype.hasOwnProperty.call(changes, field)) {
          const errors = doValidate(change.type, field, changes[field]);

          if (errors) {
            console.log(change, changes);
            dispatch(addValidationError(change.ref, field, errors));
            successful = false;
          }
        }
      }
    }
  });

  return successful;
};

export const clearChange = (ref) => (dispatch) => dispatch({ type: types.CLEAR_PENDING_CHANGE, payload: { ref } });
export const clearChanges = () => (dispatch) => dispatch({ type: types.CLEAR_PENDING_CHANGES });

// Save/Sync all changes to the server
export const saveChanges = (allChanges, id = '') => async (dispatch) => {
  const changes = sortBy(
    allChanges.filter(
      (change) => (
        (change.required && Object.keys(change.required).length > 0)
        || (change.changes && Object.keys(change.changes).length > 0)
      )
    ),
    ['sort']
  );

  dispatch(clearValidationErrors());
  if (!dispatch(validateChanges(changes))) {
    throw new Error('Some validation errors during save.');
  }
  dispatch(saveClearErrors());
  dispatch(saveStatusStart(changes.length));

  let assetId = id;
  let errorsEncountered = false;

  const callApi = async (call) => {
    let response;

    try {
      switch (call.verb) {
        case 'POST':
          response = await api.createResource(call.url, call.data);
          dispatch(updateChangeId(call.ref, decodePostResponseId(call.type, response)));
          break;
        case 'PUT':
          response = await api.updateResource(call.url, call.data);
          break;
        case 'DELETE':
          response = await api.deleteResource(call.url);
          break;
        default:
        // no
      }
    } catch (e) {
      dispatch(saveAddError({ description: call.description, error: e }));
      errorsEncountered = true;
      return e;
    }

    if (call.type === 'facilities' || call.type === 'recareas') {
      assetId = response.data.FACILITYID || response.data.RECAREAID;
    }

    dispatch(saveStatusStep());
    dispatch(clearChange(call.ref));

    return response;
  };

  await changes
    // Filter out any changes that don't have any changes.
    .filter((change) => change.changes && Object.keys(change.changes).length > 0)
    // Fancy code for "call callApi sequentially with all changes and the assetId that may or may not have been generated
    // by the first change (or any previous change really)". Ugh! My head hurts now. =-P
    .reduce((all, change) => all.then((v) => {
    let call;
    try {
      call = buildCall(change, assetId);
    } catch (e) {
      for (const err of e) {
        dispatch(addValidationError(change.ref, err.field, err.messages));
      }
      errorsEncountered = true;
      return Promise.all([...v]);
    }

    return Promise.all([...v, callApi(call)]);
  }), Promise.resolve([]));

  dispatch(saveStatusClear());

  if (errorsEncountered) {
    throw new Error('Some errors during save.');
  }

  return Promise.resolve(assetId);
};
